import { useEffect, useState } from "react";
import { IndianRupee, ShoppingBag, TrendingUp, Users } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, inr } from "@/lib/api";

const PINK = "#E80071";
const COLORS = ["#E80071", "#18181b", "#f9a8d4", "#a1a1aa", "#B00058", "#e4e4e7", "#fda4af"];

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-3 text-gold">
      <Icon size={17} />
      <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">{label}</span>
    </div>
    <p className="font-serif text-4xl text-zinc-900 mt-4">{value}</p>
  </div>
);

export const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [salePerf, setSalePerf] = useState(null);
  const [reels, setReels] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/analytics/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/admin/analytics/sale-performance").then((r) => setSalePerf(r.data)).catch(() => {});
    api.get("/admin/analytics/reels").then((r) => setReels(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-zinc-400 text-sm py-12">Crunching numbers…</p>;

  return (
    <div className="space-y-10" data-testid="dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={IndianRupee} label="Revenue" value={inr(data.revenue)} testid="stat-revenue" />
        <Stat icon={ShoppingBag} label="Orders" value={data.orders} testid="stat-orders" />
        <Stat icon={TrendingUp} label="Avg Order" value={inr(data.avg_order)} testid="stat-avg" />
        <Stat icon={Users} label="Customers" value={data.customers} testid="stat-customers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Revenue — last 30 days</p>
          {data.revenue_by_day.length === 0 ? (
            <p className="font-serif italic text-lg text-zinc-400 py-16 text-center">Sales will draw themselves here.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.revenue_by_day}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PINK} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={PINK} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} width={50} />
                <Tooltip formatter={(v) => [inr(v), "Revenue"]} contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
                <Area type="monotone" dataKey="revenue" stroke={PINK} strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Orders by status</p>
          {data.orders_by_status.length === 0 ? (
            <p className="font-serif italic text-lg text-zinc-400 py-16 text-center">No orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.orders_by_status} dataKey="count" nameKey="status" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.orders_by_status.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            {data.orders_by_status.map((s, i) => (
              <span key={s.status} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                {s.status.replace(/_/g, " ")} ({s.count})
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Top products by revenue</p>
        {data.top_products.length === 0 ? (
          <p className="font-serif italic text-lg text-zinc-400 py-10 text-center">Your bestsellers will appear here.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, data.top_products.length * 44)}>
            <BarChart data={data.top_products} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#a1a1aa" }} tickFormatter={(v) => `₹${v}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} width={220} tickFormatter={(n) => (n.length > 32 ? n.slice(0, 32) + "…" : n)} />
              <Tooltip formatter={(v) => [inr(v), "Revenue"]} contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
              <Bar dataKey="revenue" fill={PINK} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {data.returns && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-testid="returns-analytics">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Returns & refunds</p>
            <span className="text-[10px] uppercase tracking-[0.2em] bg-purple-50 text-purple-600 px-2.5 py-1 rounded-full" data-testid="return-rate-badge">{data.returns.return_rate}% return rate</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Returned orders</p>
              <p className="font-serif text-3xl text-zinc-900 mt-2" data-testid="returns-count">{data.returns.returned_orders}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Refunded total</p>
              <p className="font-serif text-3xl text-zinc-900 mt-2" data-testid="returns-refund-total">{inr(data.returns.refund_total)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Wallet refunded</p>
              <p className="font-serif text-3xl text-emerald-600 mt-2" data-testid="returns-wallet">{inr(data.returns.wallet_refunded)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Pending refunds</p>
              <p className="font-serif text-3xl text-amber-600 mt-2" data-testid="returns-pending">{inr(data.returns.pending_refunds)}</p>
            </div>
          </div>
          {data.returns.recent.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 border-b border-zinc-100">
                    <th className="pb-3 pr-4 font-medium">Order</th>
                    <th className="pb-3 pr-4 font-medium">Customer</th>
                    <th className="pb-3 pr-4 font-medium">Order total</th>
                    <th className="pb-3 pr-4 font-medium">Refund</th>
                    <th className="pb-3 font-medium">Refund status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.returns.recent.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-50 last:border-0" data-testid={`return-row-${r.id.slice(0, 8)}`}>
                      <td className="py-3 pr-4 text-sm font-medium text-[#E80071]">#{r.id.slice(0, 8).toUpperCase()}</td>
                      <td className="py-3 pr-4 text-sm text-zinc-700">{r.customer}</td>
                      <td className="py-3 pr-4 text-sm text-zinc-700">{inr(r.total)}</td>
                      <td className="py-3 pr-4 text-sm text-zinc-700">{r.refund_amount ? inr(r.refund_amount) : "—"}</td>
                      <td className="py-3">
                        <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${
                          r.refund_status === "refunded_wallet" ? "bg-emerald-50 text-emerald-600"
                          : r.refund_status === "pending_stripe" ? "bg-amber-50 text-amber-600"
                          : r.refund_status === "pending_manual" ? "bg-amber-50 text-amber-600"
                          : "bg-zinc-100 text-zinc-500"
                        }`}>
                          {{ refunded_wallet: "Wallet credited", pending_stripe: "Process in Stripe", pending_manual: "Manual", not_required: "No refund due" }[r.refund_status] || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-testid="bundle-analytics">
        <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Bundle performance — Frequently Bought Together</p>
        {bundles.length === 0 ? (
          <p className="font-serif italic text-lg text-zinc-400 py-10 text-center">Bundle adds will appear here once shoppers use the Frequently Bought Together row.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 border-b border-zinc-100">
                  <th className="pb-3 pr-4 font-medium">Bundle</th>
                  <th className="pb-3 pr-4 font-medium">Added to bag</th>
                  <th className="pb-3 pr-4 font-medium">Converted</th>
                  <th className="pb-3 font-medium">Rate</th>
                </tr>
              </thead>
              <tbody>
                {bundles.map((b, i) => (
                  <tr key={i} className="border-b border-zinc-50 last:border-0" data-testid={`bundle-row-${i}`}>
                    <td className="py-3.5 pr-4">
                      <p className="text-sm text-zinc-900">{b.anchor_name}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">+ {b.pair_names.join(" + ")}</p>
                    </td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-700">{b.adds}</td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-700">{b.conversions}</td>
                    <td className="py-3.5">
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${b.rate >= 50 ? "bg-emerald-50 text-emerald-600" : b.rate >= 20 ? "bg-amber-50 text-amber-600" : "bg-zinc-100 text-zinc-500"}`}>
                        {b.rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {salePerf && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-testid="sale-performance">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Flash sale performance</p>
            <span className="text-[10px] uppercase tracking-[0.2em] bg-zinc-100 text-zinc-500 px-2.5 py-1 rounded-full">{salePerf.windows} sale window{salePerf.windows === 1 ? "" : "s"} run</span>
          </div>
          {salePerf.windows === 0 ? (
            <p className="font-serif italic text-lg text-zinc-400 py-6 text-center">Run your first flash sale from Discounts → Flash Sale, then compare it against normal days here.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">During sale days</p>
                <p className="font-serif text-3xl text-zinc-900 mt-2" data-testid="sale-avg">{inr(salePerf.sale.avg_daily)}<span className="text-sm text-zinc-400">/day</span></p>
                <p className="text-xs text-zinc-400 mt-1">{salePerf.sale.orders} orders across {salePerf.sale.days} day{salePerf.sale.days === 1 ? "" : "s"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Normal days</p>
                <p className="font-serif text-3xl text-zinc-900 mt-2" data-testid="normal-avg">{inr(salePerf.normal.avg_daily)}<span className="text-sm text-zinc-400">/day</span></p>
                <p className="text-xs text-zinc-400 mt-1">{salePerf.normal.orders} orders across {salePerf.normal.days} day{salePerf.normal.days === 1 ? "" : "s"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Sale lift</p>
                <p className={`font-serif text-3xl mt-2 ${salePerf.lift_pct >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="sale-lift">
                  {salePerf.lift_pct >= 0 ? "+" : ""}{salePerf.lift_pct}%
                </p>
                <p className="text-xs text-zinc-400 mt-1">daily revenue vs normal days</p>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-testid="reel-analytics">
        <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Shoppable reel performance</p>
        {reels.length === 0 ? (
          <p className="font-serif italic text-lg text-zinc-400 py-6 text-center">Reel add-to-bags will appear here once shoppers use the Instagram slider or product page reels.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 border-b border-zinc-100">
                  <th className="pb-3 pr-4 font-medium">Reel</th>
                  <th className="pb-3 pr-4 font-medium">Tagged product</th>
                  <th className="pb-3 pr-4 font-medium">Homepage</th>
                  <th className="pb-3 pr-4 font-medium">Product page</th>
                  <th className="pb-3 pr-4 font-medium">Total adds</th>
                  <th className="pb-3 pr-4 font-medium">Views</th>
                  <th className="pb-3 font-medium">Conv. rate</th>
                </tr>
              </thead>
              <tbody>
                {reels.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-50 last:border-0" data-testid={`reel-row-${i}`}>
                    <td className="py-3.5 pr-4">
                      <a href={`https://www.instagram.com/reel/${r.reel_id}/`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#E80071] hover:underline">/reel/{r.reel_id.slice(0, 8)}…</a>
                    </td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-700">{r.product_name || "—"}</td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-600">{r.home_adds}</td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-600">{r.pdp_adds}</td>
                    <td className="py-3.5 pr-4 text-sm font-medium text-zinc-900">{r.total}</td>
                    <td className="py-3.5 pr-4 text-sm text-zinc-600">{r.views}</td>
                    <td className="py-3.5">
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${r.rate >= 10 ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-500"}`}>{r.rate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
